import React, { createContext, useContext } from 'react';
import { Toast } from 'primereact/toast';
import { useToast } from '../core/hooks/useToast';

type ToastType = 'success' | 'info' | 'warn' | 'error';

interface NotifyOptions {
  type: ToastType;
  title: string;
  content?: string;
  life?: number;
}

interface ToastContextValue {
  notify: (options: NotifyOptions) => void;
  showSuccess: (message: string, detail?: string) => void;
  showError: (message: string, detail?: string) => void;
  showInfo: (message: string, detail?: string) => void;
  showWarning: (message: string, detail?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toastRef, showToast, showSuccess, showError, showInfo, showWarning } = useToast();

  const notify = ({ type, title, content = '', life = 3000 }: NotifyOptions) => {
    showToast(type, title, content, life);
  };

  return (
    <ToastContext.Provider value={{ notify, showSuccess, showError, showInfo, showWarning }}>
      <Toast ref={toastRef} position="top-right" />
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToastContext must be used within a <ToastProvider>');
  }
  return ctx;
};
